/**
 * Centralized Firebase Analytics Event Tracking Utility
 *
 * This file provides type-safe, consistent event tracking across the application.
 * All Firebase Analytics events should be tracked through these helper functions.
 */

import { trackEventAsync as trackEvent, ANALYTICS_EVENTS } from '../services/firebaseAnalytics';
import type { ModelName } from '../constants/modelNames';

// ============================================================================
// Event Parameter Types
// ============================================================================

interface BaseEventParams {
  timestamp?: string;
  platform?: 'web';
}

interface FileUploadParams extends BaseEventParams {
  file_type?: string;
  file_size?: number;
  source?: 'device' | 'cloud' | 'url';
  provider?: string;
}

interface ChatEventParams extends BaseEventParams {
  session_id?: string;
  message_length?: number;
  has_attachments?: boolean;
  industry?: string;
  model?: ModelName;
}

interface ExportEventParams extends BaseEventParams {
  provider?: string;
  format?: string;
  destination?: string;
  export_type?: 'cloud' | 'crm' | 'database' | 'local';
}

interface ConnectionEventParams extends BaseEventParams {
  provider?: string;
  connection_type?: string;
  status?: 'success' | 'failed' | 'disconnected';
}

interface ErrorEventParams extends BaseEventParams {
  error_type?: string;
  /**
   * Do not send raw API or `Error.message` text here — it can contain PII or internal details.
   * Prefer `error_code` and `http_status` for third-party analytics.
   */
  error_message?: string;
  /** Coarse failure bucket (e.g. `http_403`, `timeout`, `http_5xx`). */
  error_code?: string;
  /** HTTP status when the failure was an API response (no raw body). */
  http_status?: number;
  component?: string;
}

interface WorkspaceEventParams extends BaseEventParams {
  workspace_id?: string;
  workspace_name?: string;
}

interface OnboardingEventParams extends BaseEventParams {
  step?: string;
  goal?: string;
}

interface ModelEventParams extends BaseEventParams {
  model_name?: string;
  industry?: string;
  is_premium?: boolean;
}

// ============================================================================
// Authentication & User Management Events
// ============================================================================

export const analyticsEvents = {
  // Authentication
  auth: {
    mfaVerificationStarted: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.MFA_VERIFICATION_STARTED, { ...params }),

    mfaVerificationCompleted: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.MFA_VERIFICATION_COMPLETED, { ...params }),

    mfaVerificationFailed: (params?: ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.MFA_VERIFICATION_FAILED, { ...params }),

    googleLoginStarted: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.GOOGLE_LOGIN_STARTED, { ...params }),

    oktaLoginStarted: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.OKTA_LOGIN_STARTED, { ...params }),

    passwordResetRequested: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.PASSWORD_RESET_REQUESTED, { ...params }),

    emailVerificationSent: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFICATION_SENT, { ...params }),

    emailVerified: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFIED, { ...params }),

    login: (params: { method: 'email' | 'email_mfa' | 'google' | 'okta' }) =>
      trackEvent(ANALYTICS_EVENTS.LOGIN, { ...params }),

    signUp: (params: { method: 'email' | 'invitation' }) =>
      trackEvent(ANALYTICS_EVENTS.SIGN_UP, { ...params }),
  },

  // Session Management
  session: {
    sessionRevoked: (params?: { session_id?: string }) =>
      trackEvent(ANALYTICS_EVENTS.SESSION_REVOKED, { ...params }),

    allSessionsRevoked: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.ALL_SESSIONS_REVOKED, { ...params }),

    logout: () => trackEvent(ANALYTICS_EVENTS.LOGOUT, {}),
  },

  email: {
    onboardingEmailCtaLanding: (params: {
      utm_campaign: string;
      utm_source: string;
      utm_content: string;
    }) => trackEvent(ANALYTICS_EVENTS.ONBOARDING_EMAIL_CTA_LANDING, { ...params }),
  },

  // ============================================================================
  // Document & File Management Events
  // ============================================================================

  document: {
    uploadStarted: (params: FileUploadParams) =>
      trackEvent(ANALYTICS_EVENTS.DOCUMENT_UPLOAD_STARTED, { ...params }),

    uploadCompleted: (params: FileUploadParams) =>
      trackEvent(ANALYTICS_EVENTS.DOCUMENT_UPLOAD_COMPLETED, { ...params }),

    uploadFailed: (params: FileUploadParams & ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.DOCUMENT_UPLOAD_FAILED, { ...params }),

    fileUploadFromCloud: (params: FileUploadParams) =>
      trackEvent(ANALYTICS_EVENTS.FILE_UPLOAD_FROM_CLOUD, { ...params }),

    fileUploadFromUrl: (params: FileUploadParams) =>
      trackEvent(ANALYTICS_EVENTS.FILE_UPLOAD_FROM_URL, { ...params }),

    fileUploadFromDevice: (params: FileUploadParams) =>
      trackEvent(ANALYTICS_EVENTS.FILE_UPLOAD_FROM_DEVICE, { ...params }),

    documentDeleted: (params?: { document_id?: string }) =>
      trackEvent(ANALYTICS_EVENTS.DOCUMENT_DELETED, { ...params }),

    documentDownloaded: (params?: { document_id?: string; format?: string }) =>
      trackEvent(ANALYTICS_EVENTS.DOCUMENT_DOWNLOADED, { ...params }),
  },

  // ============================================================================
  // Chat & Messaging Events
  // ============================================================================

  chat: {
    sessionStarted: (params: ChatEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CHAT_SESSION_STARTED, { ...params }),

    messageSent: (params: ChatEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_SENT, { ...params }),

    responseReceived: (params: ChatEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CHAT_RESPONSE_RECEIVED, { ...params }),

    sessionDeleted: (params?: { session_id?: string }) =>
      trackEvent(ANALYTICS_EVENTS.CHAT_SESSION_DELETED, { ...params }),

    sessionRenamed: (params?: { session_id?: string }) =>
      trackEvent(ANALYTICS_EVENTS.CHAT_SESSION_RENAMED, { ...params }),

    historyViewed: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CHAT_HISTORY_VIEWED, { ...params }),

    errorOccurred: (params: ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CHAT_ERROR_OCCURRED, { ...params }),

    /** Structured document summary / extraction completed (count in GA4 as "documents summarized"). */
    documentSummarized: (params?: { form_type?: string; industry?: string }) =>
      trackEvent(ANALYTICS_EVENTS.DOCUMENT_SUMMARIZED, { ...params }),
  },

  // ============================================================================
  // Industry & Model Selection Events
  // ============================================================================

  industry: {
    selected: (params: { industry?: string }) =>
      trackEvent(ANALYTICS_EVENTS.INDUSTRY_SELECTED, { ...params }),

    formGenerated: (params: { industry?: string; form_type?: string }) =>
      trackEvent(ANALYTICS_EVENTS.INDUSTRY_FORM_GENERATED, { ...params }),
  },

  model: {
    selected: (params: ModelEventParams) =>
      trackEvent(ANALYTICS_EVENTS.MODEL_SELECTED, { ...params }),

    upgraded: (params: ModelEventParams) =>
      trackEvent(ANALYTICS_EVENTS.MODEL_UPGRADED, { ...params }),
  },

  // ============================================================================
  // Export & Integration Events
  // ============================================================================

  export: {
    started: (params: ExportEventParams) =>
      trackEvent(ANALYTICS_EVENTS.EXPORT_STARTED, { ...params }),

    completed: (params: ExportEventParams) =>
      trackEvent(ANALYTICS_EVENTS.EXPORT_COMPLETED, { ...params }),

    failed: (params: ExportEventParams & ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.EXPORT_FAILED, { ...params }),

    // Cloud Export
    cloudStarted: (params: ExportEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CLOUD_EXPORT_STARTED, {
        ...params,
        export_type: 'cloud',
      }),

    cloudCompleted: (params: ExportEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CLOUD_EXPORT_COMPLETED, {
        ...params,
        export_type: 'cloud',
      }),

    cloudFailed: (params: ExportEventParams & ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CLOUD_EXPORT_FAILED, {
        ...params,
        export_type: 'cloud',
      }),

    // CRM Export
    crmStarted: (params: ExportEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CRM_EXPORT_STARTED, {
        ...params,
        export_type: 'crm',
      }),

    crmCompleted: (params: ExportEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CRM_EXPORT_COMPLETED, {
        ...params,
        export_type: 'crm',
      }),

    crmFailed: (params: ExportEventParams & ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CRM_EXPORT_FAILED, {
        ...params,
        export_type: 'crm',
      }),

    // Database Export
    databaseStarted: (params: ExportEventParams) =>
      trackEvent(ANALYTICS_EVENTS.DATABASE_EXPORT_STARTED, {
        ...params,
        export_type: 'database',
      }),

    databaseCompleted: (params: ExportEventParams) =>
      trackEvent(ANALYTICS_EVENTS.DATABASE_EXPORT_COMPLETED, {
        ...params,
        export_type: 'database',
      }),

    databaseFailed: (params: ExportEventParams & ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.DATABASE_EXPORT_FAILED, {
        ...params,
        export_type: 'database',
      }),
  },

  // ============================================================================
  // Cloud Connections Events
  // ============================================================================

  connection: {
    initiated: (params: ConnectionEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CLOUD_CONNECTION_INITIATED, { ...params }),

    completed: (params: ConnectionEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CLOUD_CONNECTION_COMPLETED, {
        ...params,
        status: 'success',
      }),

    failed: (params: ConnectionEventParams & ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CLOUD_CONNECTION_FAILED, {
        ...params,
        status: 'failed',
      }),

    disconnected: (params: ConnectionEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CLOUD_CONNECTION_DISCONNECTED, {
        ...params,
        status: 'disconnected',
      }),

    reconnected: (params: ConnectionEventParams) =>
      trackEvent(ANALYTICS_EVENTS.CLOUD_CONNECTION_RECONNECTED, {
        ...params,
      }),
  },

  // ============================================================================
  // Workspace Management Events
  // ============================================================================

  workspace: {
    created: (params: WorkspaceEventParams) =>
      trackEvent(ANALYTICS_EVENTS.WORKSPACE_CREATED, { ...params }),

    selected: (params: WorkspaceEventParams) =>
      trackEvent(ANALYTICS_EVENTS.WORKSPACE_SELECTED, { ...params }),

    deleted: (params: WorkspaceEventParams) =>
      trackEvent(ANALYTICS_EVENTS.WORKSPACE_DELETED, { ...params }),

    renamed: (params: WorkspaceEventParams) =>
      trackEvent(ANALYTICS_EVENTS.WORKSPACE_RENAMED, { ...params }),
  },

  // ============================================================================
  // Onboarding & Goals Events
  // ============================================================================

  onboarding: {
    started: (params?: OnboardingEventParams) =>
      trackEvent(ANALYTICS_EVENTS.ONBOARDING_STARTED, { ...params }),

    completed: (params?: OnboardingEventParams) =>
      trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETED, { ...params }),

    skipped: (params?: OnboardingEventParams) =>
      trackEvent(ANALYTICS_EVENTS.ONBOARDING_SKIPPED, { ...params }),

    goalSelected: (params: OnboardingEventParams) =>
      trackEvent(ANALYTICS_EVENTS.GOAL_SELECTED, { ...params }),

    goalCompleted: (params: OnboardingEventParams) =>
      trackEvent(ANALYTICS_EVENTS.GOAL_COMPLETED, { ...params }),
  },

  // ============================================================================
  // Subscription & Billing Events
  // ============================================================================

  subscription: {
    pricingViewed: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.PRICING_PAGE_VIEWED, { ...params }),

    planSelected: (params?: { plan_name?: string; billing_cycle?: 'monthly' | 'annual' }) =>
      trackEvent(ANALYTICS_EVENTS.PLAN_SELECTED, { ...params }),

    billingCycleToggled: (params?: { from?: string; to?: string }) =>
      trackEvent(ANALYTICS_EVENTS.BILLING_CYCLE_TOGGLED, { ...params }),

    upgraded: (params?: { from_plan?: string; to_plan?: string }) =>
      trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_UPGRADED, { ...params }),

    downgraded: (params?: { from_plan?: string; to_plan?: string }) =>
      trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_DOWNGRADED, { ...params }),

    cancelled: (params?: { plan_name?: string }) =>
      trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_CANCELLED, { ...params }),

    paymentMethodAdded: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.PAYMENT_METHOD_ADDED, { ...params }),

    paymentMethodUpdated: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.PAYMENT_METHOD_UPDATED, { ...params }),

    invoiceDownloaded: (params?: { invoice_id?: string }) =>
      trackEvent(ANALYTICS_EVENTS.INVOICE_DOWNLOADED, { ...params }),
  },

  // ============================================================================
  // Profile & Settings Events
  // ============================================================================

  profile: {
    viewed: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.PROFILE_VIEWED, { ...params }),

    updated: (params?: { fields_updated?: string }) =>
      trackEvent(ANALYTICS_EVENTS.PROFILE_UPDATED, { ...params }),

    passwordChanged: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.PASSWORD_CHANGED, { ...params }),

    mfaEnabled: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.MFA_ENABLED, { ...params }),

    mfaDisabled: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.MFA_DISABLED, { ...params }),

    notificationSettingsUpdated: (params?: BaseEventParams) =>
      trackEvent(ANALYTICS_EVENTS.NOTIFICATION_SETTINGS_UPDATED, {
        ...params,
      }),

    languageChanged: (params?: { from_language?: string; to_language?: string }) =>
      trackEvent(ANALYTICS_EVENTS.LANGUAGE_CHANGED, { ...params }),
  },

  // ============================================================================
  // Form & Data Extraction Events
  // ============================================================================

  form: {
    dataExtracted: (params?: { form_type?: string; industry?: string }) =>
      trackEvent(ANALYTICS_EVENTS.FORM_DATA_EXTRACTED, { ...params }),

    dataEdited: (params?: { form_type?: string; field_name?: string }) =>
      trackEvent(ANALYTICS_EVENTS.FORM_DATA_EDITED, { ...params }),

    validationFailed: (params?: { form_type?: string; errors?: string }) =>
      trackEvent(ANALYTICS_EVENTS.FORM_VALIDATION_FAILED, { ...params }),

    submitted: (params?: { form_type?: string }) =>
      trackEvent(ANALYTICS_EVENTS.FORM_SUBMITTED, { ...params }),
  },

  // ============================================================================
  // Error & Performance Events
  // ============================================================================

  error: {
    occurred: (params: ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, { ...params }),

    apiError: (params: ErrorEventParams) => trackEvent(ANALYTICS_EVENTS.API_ERROR, { ...params }),

    networkError: (params: ErrorEventParams) =>
      trackEvent(ANALYTICS_EVENTS.NETWORK_ERROR, { ...params }),
  },

  // ============================================================================
  // Feature Usage Events
  // ============================================================================

  feature: {
    discovered: (params?: { feature_name?: string }) =>
      trackEvent(ANALYTICS_EVENTS.FEATURE_DISCOVERED, { ...params }),

    helpAccessed: (params?: { help_topic?: string }) =>
      trackEvent(ANALYTICS_EVENTS.HELP_ACCESSED, { ...params }),

    feedbackSubmitted: (params?: { feedback_type?: string }) =>
      trackEvent(ANALYTICS_EVENTS.FEEDBACK_SUBMITTED, { ...params }),
  },
};

// Export for backward compatibility
export default analyticsEvents;
